import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator, View } from '@aws-amplify/ui-react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import '@aws-amplify/ui-react/styles.css';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { Container, Typography, Grid, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Help as HelpIcon } from '@mui/icons-material';
import awsconfig from './aws-exports';

import Navigation from './Navigation';

//Configuration Pages
import ProxyConfiguration from './pages/ProxyConfiguration';
import WebsitesConfiguration from './pages/WebsitesConfiguration';

//Model Management and Testing
import ModelDetails from './pages/ModelDetails';


//Consumer Page
import TimeMachine from './pages/TimeMachine';

//Ops Screens
import ScreenshotErrors from './pages/ScreenshotErrors'
import GameNames from './pages/GameNames';
import BrandsOperators from './pages/BrandsOperators';
import AuditOverview from './pages/AuditOverview';
import GameManagement from './pages/GameManagement';
import PromoManagement from './pages/PromoManagement';
import PortalManager from './pages/PortalManager';
import StudioFinder from './pages/StudioFinder';
import AnomalyReview from './pages/AnomalyReview';
import DataLakeOverview from './pages/DataLakeOverview';

//Tiles
import URLBoxStatusTile from './ui-components/URLBoxStatusTile';
import BrightDataStatusTile from './ui-components/BrightDataStatusTile';
import SimplyNodeStatusTile from './ui-components/SimplyNodeStatusTile';
import DataLakeStatusTile from './ui-components/DataLakeStatusTile';
import { GamesProvider } from './contexts/GamesContext';


// Configure Amplify
Amplify.configure(awsconfig);

const App = () => {
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewLang, setWhatsNewLang] = useState('en');

  useEffect(() => {
    if (process.env.REACT_APP_ENV && process.env.REACT_APP_ENV.toLowerCase() === 'dev') {
      document.title = "DEV - Product Radars Backoffice";
    }
    const seen = localStorage.getItem('whats_new_seen_home_v1');
    if (!seen) {
      setWhatsNewOpen(true);
    }
  }, []);

  const handleCloseWhatsNew = () => setWhatsNewOpen(false);
  const handleDontShowWhatsNew = () => {
    localStorage.setItem('whats_new_seen_home_v1', 'true');
    setWhatsNewOpen(false);
  };

  return (
    <GamesProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Authenticator>
            {({ signOut, user }) => (
              <>
                {user && <Navigation signOut={signOut} />}
                <Container>
                <View as="main">
                  <Routes>
                    <Route path="/proxy-configuration" element={<ProxyConfiguration />} />
                    <Route path="/time-machine" element={<TimeMachine />} />
                    <Route path="/websites-configuration" element={<WebsitesConfiguration />} />
                    <Route path="/model-details" element={<ModelDetails />} />
                    <Route path="/game-names" element={<GameNames />} />
                    <Route path="/game-management" element={<GameManagement />} />
                    <Route path="/brands-operators" element={<BrandsOperators />} />
                    <Route path="/screenshot-errors" element={<ScreenshotErrors />} />

                    <Route path="/promo-management" element={<PromoManagement />} />
                    <Route path="/audit-overview" element={<AuditOverview />} />
                    <Route path="/portal-manager" element={<PortalManager />} />
                    <Route path="/studio-finder" element={<StudioFinder />} />
                    <Route path="/anomaly-review" element={<AnomalyReview />} />
                    <Route path="/data-lake" element={<DataLakeOverview />} />
                    <Route
                      path="/"
                      element={
                        <>
                          {user && (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h5">
                                  Welcome to the {process.env.REACT_APP_ENV && process.env.REACT_APP_ENV.toLowerCase() === 'dev' ? 'DEV ' : ''}Product Radars Back Office
                                </Typography>
                                <IconButton 
                                    onClick={() => setWhatsNewOpen(true)} 
                                    size="small" 
                                    color="primary"
                                    sx={{ ml: 1, border: 1, borderColor: 'primary.light', p: 0.5 }}
                                    title="What's New Guide"
                                >
                                    <HelpIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Grid container spacing={2}>
                                {/* Apply flexbox and flex: 1 for equal height */}
                                <Grid item xs={12} sm={3} sx={{ display: 'flex' }}>
                                  <URLBoxStatusTile sx={{ flex: 1 }} />
                                </Grid>
                                <Grid item xs={12} sm={3} sx={{ display: 'flex' }}>
                                  <BrightDataStatusTile sx={{ flex: 1 }} />
                                </Grid>
                                <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                                  <SimplyNodeStatusTile sx={{ flex: 1 }} />
                                </Grid>
                                <Grid item xs={12} sm={12} sx={{ display: 'flex' }}>
                                  <DataLakeStatusTile sx={{ flex: 1 }} />
                                </Grid>

                              </Grid>
                            </> 
                          )}
                        </>
                      }
                    />
                  </Routes>
                </View>
              </Container>
              
              {/* Home Page What's New Dialog */}
              <Dialog 
                  open={whatsNewOpen} 
                  onClose={handleCloseWhatsNew}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{
                      sx: { borderRadius: 3, boxShadow: 24, p: 1 }
                  }}
              >
                  <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>✨</span> {whatsNewLang === 'en' ? "What's New in the Back Office!" : "Novidades no Back Office!"}
                      </Typography>
                      <ToggleButtonGroup
                          value={whatsNewLang}
                          exclusive
                          onChange={(e, val) => val && setWhatsNewLang(val)}
                          size="small"
                          sx={{
                              '& .MuiToggleButton-root': {
                                  py: 0.2, px: 1.5, fontSize: '1.2rem', borderRadius: '8px',
                              }
                          }}
                      >
                          <ToggleButton value="en" title="English">🇬🇧</ToggleButton>
                          <ToggleButton value="pt" title="Português (Brasil)">🇧🇷</ToggleButton>
                      </ToggleButtonGroup>
                  </DialogTitle>
                  <DialogContent>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                          {whatsNewLang === 'en' 
                              ? "We're constantly adding new tools to help you manage the platform. Check out the latest addition!"
                              : "Estamos constantemente adicionando novas ferramentas para ajudar você a gerenciar a plataforma. Confira a novidade!"}
                      </Typography>
                      
                      <Stack spacing={2.5}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              <Box sx={{ fontSize: '1.75rem', bgcolor: 'rgba(156, 39, 176, 0.1)', p: 1, borderRadius: 2, lineHeight: 1 }}>
                                  🕵️‍♂️
                              </Box>
                              <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      {whatsNewLang === 'en' ? "New Anomaly Review Workspace" : "Novo Espaço de Revisão de Anomalias"}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                      {whatsNewLang === 'en' 
                                          ? "Jump into the new Anomaly Review page to quickly audit lists of misidentified games and efficiently cluster duplicates together. Use Smart Lists to track common AI mistakes!"
                                          : "Acesse a nova página de Revisão de Anomalias para auditar rapidamente listas de jogos identificados incorretamente e agrupar duplicatas com eficiência. Use Listas Inteligentes para rastrear erros comuns de IA!"}
                                  </Typography>
                              </Box>
                          </Box>
                      </Stack>
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                      <Button onClick={handleDontShowWhatsNew} sx={{ textTransform: 'none', color: '#666' }}>
                          {whatsNewLang === 'en' ? "Exit, and Don't Show Again" : "Sair e Não Mostrar Novamente"}
                      </Button>
                      <Button variant="contained" onClick={handleCloseWhatsNew} sx={{ px: 3, borderRadius: 2 }}>
                          {whatsNewLang === 'en' ? "Exit" : "Sair"}
                      </Button>
                  </DialogActions>
              </Dialog>
            </>
          )}
        </Authenticator>
      </Router>
    </ThemeProvider>
    </GamesProvider>
  );
};

export default App;